import { Tab, TabList } from '@ardoq/tabs';
import { Header4, InlineText } from '@ardoq/typography';
import { CreatableSelect } from '@ardoq/select';
import { TextInput } from '@ardoq/forms';
import styled from 'styled-components';
import { s16, s32 } from '@ardoq/design-tokens';
import { GhostButton, PrimaryButton, SecondaryButton } from '@ardoq/button';
import { Island } from '@ardoq/page-layout';
import { Box, FlexBox, Stack } from '@ardoq/layout';
import { ComponentTypes } from './types';
import { Fragment, useState } from 'react';
import { Icon, IconName } from '@ardoq/icons';
import { type I18nLocale, t } from '@ardoq/i18n';

type MapFieldsProps = {
  i18nLocale: I18nLocale;
  integrationName: string;
  fieldMappingIsValid: boolean;
  componentTypes: ComponentTypes[];
  onNext: () => void;
  moreButtonLabel?: string;
  islandTitle?: string;
  nextButtonLabel?: string;
  selectPlaceholder?: string;
  selectFieldPlaceholder?: string;
  mapLabel?: string;
  toLabel?: string;
};

export function MapFields({
  i18nLocale,
  integrationName,
  fieldMappingIsValid,
  componentTypes,
  onNext,
  moreButtonLabel,
  islandTitle = t(
    i18nLocale,
    'integrations:mapFields.islandTitle',
    { integrationName },
    'Configure components mapping to export to {{integrationName}}'
  ),
  nextButtonLabel = t(
    i18nLocale,
    'integrations:mapFields.nextButtonLabel',
    'Next: Review export'
  ),
  selectPlaceholder = t(
    i18nLocale,
    'integrations:mapFields.selectPlaceholder',
    'Select or start typing'
  ),
  selectFieldPlaceholder = t(
    i18nLocale,
    'integrations:mapFields.selectFieldPlaceholder',
    'Select a field'
  ),
  mapLabel = t(i18nLocale, 'integrations:mapFields.mapLabel', 'Map'),
  toLabel = t(i18nLocale, 'integrations:mapFields.toLabel', 'to'),
}: MapFieldsProps) {
  const [activeTabId, setActiveTabId] = useState<string>();
  const activeComponentType =
    componentTypes.find(({ label }) => label === activeTabId) ??
    componentTypes[0];
  return (
    <FlexBox flexDirection="column" padding="xlarge" align="center">
      <Island
        title={islandTitle}
        minWidth="100%"
        footerContent={
          <FlexBox justify="end" flex={1}>
            <PrimaryButton isDisabled={!fieldMappingIsValid} onClick={onNext}>
              {nextButtonLabel}
            </PrimaryButton>
          </FlexBox>
        }
      >
        {componentTypes.length > 0 && (
          <>
            <Box padding="xsmall">
              <TabList
                activeTabId={activeComponentType.label}
                onTabChange={setActiveTabId}
                moreButtonLabel={moreButtonLabel}
              >
                {componentTypes.map(({ label }) => (
                  <Tab label={label} tabId={label} key={label} />
                ))}
              </TabList>
            </Box>
            <Box overflow="auto">
              <ComponentTypeMapping
                componentType={activeComponentType}
                selectPlaceholder={selectPlaceholder}
                selectFieldPlaceholder={selectFieldPlaceholder}
                mapLabel={mapLabel}
                toLabel={toLabel}
              />
            </Box>
          </>
        )}
      </Island>
    </FlexBox>
  );
}

type ComponentTypeMappingProps = {
  componentType: ComponentTypes;
  selectPlaceholder: string;
  selectFieldPlaceholder: string;
  mapLabel: string;
  toLabel: string;
};

const ComponentTypeMapping = ({
  componentType: { label, steps },
  selectPlaceholder,
  selectFieldPlaceholder,
  mapLabel,
  toLabel,
}: ComponentTypeMappingProps) => (
  <Box padding="xxsmall">
    <Stack gap="large">
      <Grid>
        {steps.map((step, i) => (
          <Fragment key={`${label}-${i}`}>
            <StepHeading>
              {i + 1}. {step.label}
            </StepHeading>
            <>
              {step.fields.map((field, j) => (
                <Fragment key={`${label}-${i}-${j}`}>
                  {field.type === 'SingleSelect' && (
                    <SingleSelectContainer>
                      <CreatableSelect
                        placeholder={selectPlaceholder}
                        value={field.value}
                        onChange={field.onChange}
                        onCreateOption={field.onCreateOption}
                        errorMessage={field.errorMessage}
                        isLoading={field.isLoading}
                        options={field.options}
                        isClearable={field.isClearable}
                      />
                    </SingleSelectContainer>
                  )}
                  {field.type === 'Mapping' && (
                    <>
                      <GridLabel>
                        <InlineText variant="text1">{mapLabel}</InlineText>
                      </GridLabel>
                      <TextInput isReadOnly value={field.from.label} />
                      <GridLabel>
                        <InlineText variant="text1">{toLabel}</InlineText>
                      </GridLabel>
                      {field.to.isReadOnly && (
                        <TextInput value={field.to.value} isReadOnly />
                      )}
                      {!field.to.isReadOnly && (
                        <CreatableSelect
                          placeholder={selectPlaceholder}
                          value={field.to.value}
                          isDisabled={field.to.isDisabled}
                          onChange={field.to.onChange}
                          onCreateOption={field.to.onCreateOption}
                          isLoading={field.to.isLoading}
                          errorMessage={field.to.errorMessage}
                          options={field.to.options}
                          isClearable={field.to.isClearable}
                          isValidNewOption={field.to.isValidNewOption}
                        />
                      )}
                      <Box />
                    </>
                  )}
                  {field.type === 'DynamicMapping' && (
                    <>
                      <GridLabel>
                        <InlineText variant="text1">{mapLabel}</InlineText>
                      </GridLabel>
                      <CreatableSelect
                        placeholder={
                          field.from.placeholder || selectFieldPlaceholder
                        }
                        value={field.from.value}
                        isDisabled={field.from.isDisabled}
                        onChange={field.from.onChange}
                        onCreateOption={field.from.onCreateOption}
                        isLoading={field.from.isLoading}
                        errorMessage={field.from.errorMessage}
                        options={field.from.options}
                        isClearable={field.from.isClearable}
                        isValidNewOption={field.from.isValidNewOption}
                      />
                      <GridLabel>
                        <InlineText variant="text1">{toLabel}</InlineText>
                      </GridLabel>
                      <CreatableSelect
                        placeholder={
                          field.to.placeholder || selectFieldPlaceholder
                        }
                        value={field.to.value}
                        isDisabled={field.to.isDisabled}
                        onChange={field.to.onChange}
                        onCreateOption={field.to.onCreateOption}
                        isLoading={field.to.isLoading}
                        errorMessage={field.to.errorMessage}
                        options={field.to.options}
                        isClearable={field.to.isClearable}
                        isValidNewOption={field.to.isValidNewOption}
                      />
                      {field.onRemove ? (
                        <Box>
                          <GhostButton onClick={field.onRemove}>
                            <Icon iconName={IconName.CLOSE} />
                          </GhostButton>
                        </Box>
                      ) : (
                        <Box />
                      )}
                    </>
                  )}
                  {field.type === 'LabeledSelect' && (
                    <>
                      {field.leadingLabel ? (
                        <GridLabel>
                          <InlineText variant="text1">
                            {field.leadingLabel}
                          </InlineText>
                        </GridLabel>
                      ) : (
                        <Box />
                      )}
                      <CreatableSelect
                        isClearable={field.isClearable ?? true}
                        placeholder={field.placeholder}
                        value={field.value}
                        options={field.options}
                        onChange={field.onChange}
                        onCreateOption={field.onCreateOption}
                      />
                      {field.trailingLabel ? (
                        <GridLabel>
                          <InlineText variant="text1">
                            {field.trailingLabel}
                          </InlineText>
                        </GridLabel>
                      ) : (
                        <Box />
                      )}
                      <Box />
                      <Box />
                    </>
                  )}
                </Fragment>
              ))}
              {step.addMapping && (
                <AddButtonContainer>
                  <SecondaryButton
                    onClick={step.addMapping.onClick}
                    isDisabled={step.addMapping.isDisabled}
                  >
                    <Icon iconName={IconName.ADD} />
                    {step.addMapping.label}
                  </SecondaryButton>
                </AddButtonContainer>
              )}
            </>
          </Fragment>
        ))}
      </Grid>
    </Stack>
  </Box>
);

const StepHeading = styled(Header4)`
  grid-column: span 5;
`;

const SingleSelectContainer = styled.div`
  grid-column: span 5;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: min-content 300px min-content 300px auto;
  white-space: nowrap;
  gap: ${s16} ${s32};
  align-items: start;
`;

const GridLabel = styled.div`
  display: flex;
  align-items: center;
  min-height: 36px;
`;

const AddButtonContainer = styled.div`
  grid-column: span 5;
  display: flex;
  justify-content: flex-start;
`;
